import React, { useState, useEffect } from "react";
import { fetchWrapper } from "../utils/fetchWrapper";
import { toast } from "react-hot-toast";
import { MdAdd, MdDelete, MdPerson, MdEdit, MdClose } from "react-icons/md";
import ConfirmModal from "./ui/ConfirmModal";
import "./UserManagement.css";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "SDR",
  });
  const [currentUserId, setCurrentUserId] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    detail: '',
    confirmText: 'Confirm',
    variant: 'danger',
    loading: false,
    confirmPhrase: '',
    onConfirm: null,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetchWrapper.get("/admin/users");
      // Check for standardized response shape
      const userData = res.data.data || res.data;
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setModalMode("add");
    setFormData({ email: "", password: "", role: "SDR" });
    setCurrentUserId(null);
  };

  const handleOpenEditModal = (user) => {
    setModalMode("edit");
    setFormData({ 
      email: user.email, 
      password: "", 
      role: user.role.toUpperCase() 
    });
    setCurrentUserId(user._id);
  };

  const closeModal = () => {
    setModalMode(null);
    setFormData({ email: "", password: "", role: "SDR" });
    setCurrentUserId(null);
    setIsSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modalMode === "edit") {
      // Show confirmation before updating
      setConfirmModal({
        show: true,
        title: 'Update User',
        message: `Are you sure you want to update ${formData.email}?`,
        detail: 'User credentials and role will be changed.',
        confirmText: 'Update User',
        variant: 'warning',
        loading: false,
        onConfirm: () => executeSubmit("edit"),
      });
      return;
    }

    // For add, proceed directly
    executeSubmit("add");
  };

  const executeSubmit = async (mode) => {
    setIsSubmitting(true);
    if (confirmModal.show) {
      setConfirmModal(prev => ({ ...prev, loading: true }));
    }
    try {
      let response;
      if (mode === "add") {
        response = await fetchWrapper.post("/admin/users", formData);
        toast.success(response.data.message || "User created successfully!");
      } else {
        response = await fetchWrapper.patch(`/admin/users/${currentUserId}`, formData);
        toast.success(response.data.message || "User updated successfully!");
      }
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
      closeModal();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || `Error ${mode === 'add' ? 'creating' : 'updating'} user`);
      setConfirmModal(prev => ({ ...prev, loading: false }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = (userId, email) => {
    setConfirmModal({
      show: true,
      title: 'Delete User',
      message: `Are you sure you want to delete ${email}?`,
      detail: 'This action cannot be undone. The user will lose all access.',
      confirmText: 'Delete User',
      variant: 'danger',
      loading: false,
      confirmPhrase: 'confirm',
      onConfirm: () => executeDelete(userId),
    });
  };

  const executeDelete = async (userId) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetchWrapper.delete(`/admin/users/${userId}`);
      toast.success(response.data.message || "User deleted successfully");
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error deleting user");
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
  };

  return (
    <div className="user-management">
      <div className="section-header">
        <div className="section-title">
          <h3>User Management</h3>
          <p>Create and manage system users and their roles.</p>
        </div>
        <button className="btn-add-premium" onClick={handleOpenAddModal}>
          <MdAdd /> Add User
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
           <div className="spinner"></div>
           <p>Loading users...</p>
        </div>
      ) : (
        <div className="user-grid">
          {users.map((user) => (
            <div key={user._id} className="user-card-premium">
              <div className="card-top">
                <div className="user-avatar-premium">
                  <MdPerson size={28} />
                </div>
                <div className="user-info-main">
                  <p className="user-email-text">{user.email}</p>
                  <div className={`role-tag role-${user.role.toLowerCase()}`}>
                    {user.role}
                  </div>
                </div>
              </div>
              <div className="card-actions-premium">
                {user.role !== "SUPERADMIN" && (
                  <>
                    <button
                      className="btn-action-edit"
                      onClick={() => handleOpenEditModal(user)}
                      title="Edit User"
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      className="btn-action-delete"
                      onClick={() => handleDeleteUser(user._id, user.email)}
                      title="Delete User"
                    >
                      <MdDelete size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalMode && (
        <div className="modal-overlay-premium">
          <div className="modal-card-premium">
            <div className="modal-header-premium">
              <div className="modal-header-top">
                <h4>{modalMode === "add" ? "Add New User" : "Edit User"}</h4>
                <button className="btn-close-modal" onClick={closeModal}><MdClose /></button>
              </div>
              <p>{modalMode === "add" ? "Enter the details for the new account." : "Update information for this account."}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group-premium">
                <label>Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div className="form-group-premium">
                <label>Password</label>
                <input
                  type="password"
                  required={modalMode === "add"}
                  placeholder={modalMode === "add" ? "••••••••" : "Leave blank to keep current"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
                {modalMode === "edit" && (
                  <small className="form-hint">Only enter a password if you wish to change it.</small>
                )}
              </div>
              <div className="form-group-premium">
                <label>System Role</label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  <option value="ADMIN">ADMIN (Full Access)</option>
                  <option value="DEV">DEV (Limited Access)</option>
                  <option value="SDR">SDR (Sales Development)</option>
                </select>
              </div>
              <div className="modal-footer-premium">
                <button
                  type="button"
                  className="btn-secondary-premium"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary-premium" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : (modalMode === "add" ? "Create User" : "Save Changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        detail={confirmModal.detail}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        loading={confirmModal.loading}
        confirmPhrase={confirmModal.confirmPhrase}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default UserManagement;
